/**
 * apps/web :: lib/whatsnext-gate
 *
 * "First-pass-required" gate for the post-pass journey route (/whats-next).
 *
 * The route is a soft-paywall: the learner sees the journey roadmap only
 * after they've passed at least one exam, in any of their target states.
 * Until then the route renders a paywall card pointing them back at /exam.
 *
 * Why a separate helper instead of /api/exam/has-passed?
 *   - The existing `/api/exam/history?stateCode=XX` endpoint already
 *     returns `passed: boolean` per attempt. Walking the user's
 *     target_states and short-circuiting on the first pass is a
 *     handful of small parallel reads — well within Phase-0 budget.
 *   - Adding a dedicated cross-state endpoint is a follow-up (lives in
 *     ROADS-431-FOLLOW-UP-001). Keeping that out of this PR keeps
 *     review surface tight and avoids a backend migration.
 *
 * Failure semantics:
 *   - If every history call rejects, we surface a "couldn't verify" state
 *     so the user isn't silently locked out. The page renders an error
 *     panel with a retry button.
 *   - A single failed state is treated as `passed=false` for that state
 *     and ignored unless every state failed — partial coverage still
 *     unlocks the route if any other state had a pass.
 *
 * ROADS-431.
 */
import { getExamHistory } from "@/lib/exam-api";
import type { UsStateCode } from "@/lib/profile-api";

/** Outcome of the gate check. */
export type WhatsNextGateStatus =
  | { kind: "loading" }
  | { kind: "blocked" } // user has no passed attempt across any target state
  | { kind: "unlocked"; passedStateCode: UsStateCode } // first state we found a pass for
  | { kind: "error"; message: string };

/**
 * Walks the user's `target_states` and returns `unlocked` as soon as any
 * one has a passed exam attempt on record, otherwise `blocked`. If every
 * lookup throws we return `error` so the UI can show a retry path.
 *
 * Parallelism is bounded by the size of `target_states` — typically 1-3
 * states for an MVP user. We don't add concurrency limits here.
 */
export async function checkFirstPassGate(
  targetStates: readonly UsStateCode[],
): Promise<WhatsNextGateStatus> {
  if (targetStates.length === 0) {
    return { kind: "blocked" };
  }

  const settled = await Promise.allSettled(
    targetStates.map((code) => getExamHistory(code, 50)),
  );

  let allFailed = true;
  let firstPass: UsStateCode | null = null;
  let firstError: string | null = null;

  for (let i = 0; i < settled.length; i += 1) {
    const r = settled[i];
    if (r.status === "fulfilled") {
      allFailed = false;
      const passed = r.value.attempts.find((a) => a.passed === true);
      if (passed) {
        firstPass = targetStates[i];
        break;
      }
    } else if (firstError == null) {
      firstError = r.reason instanceof Error ? r.reason.message : "unknown error";
    }
  }

  if (firstPass != null) {
    return { kind: "unlocked", passedStateCode: firstPass };
  }
  if (allFailed) {
    return { kind: "error", message: firstError ?? "could not load exam history" };
  }
  return { kind: "blocked" };
}
